import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { siteUrl } from "@/lib/siteConfig";
import { generateLocalizedUrl } from "@/lib/slugUtils";
import { trackSEOEvents, validateSchema } from "@/lib/seoAnalytics";

interface PortfolioStructuredDataProps {
  projects: any[];
}

const PortfolioStructuredData = ({ projects }: PortfolioStructuredDataProps) => {
  const { t, i18n } = useTranslation(['portfolio', 'common']);
  
  const baseUrl = siteUrl;
  
  const currentLanguage = (i18n.language || 'en') as any;
  // Creative Work schema for portfolio projects
  const portfolioSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Portfolio Projects",
    "description": "Custom price calculators, smart forms, and automation solutions",
    "numberOfItems": projects.length,
    "itemListElement": projects.map((project, index) => ({
      "@type": "CreativeWork",
      "position": index + 1,
      "name": project.title,
      "description": project.description,
      "creator": {
        "@type": "Person",
        "name": t('common:author')
      },
      "dateCreated": "2024",
      "genre": project.category,
      "keywords": project.technologies?.join(', '),
      "audience": {
        "@type": "Audience",
        "audienceType": project.industry
      }
    }))
  };

  // Collection page schema
  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": t('portfolio:hero.title'),
    "description": t('portfolio:hero.description'),
    "url": `${baseUrl}${generateLocalizedUrl('portfolio', currentLanguage)}`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": projects.length,
      "itemListElement": projects.map((project, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "CreativeWork",
          "name": project.title,
          "description": project.description
        }
      }))
    }
  };
  
  const schemas = [portfolioSchema, collectionSchema];
  
  useEffect(() => {
    try {
      schemas.forEach((schema) => {
        if (validateSchema(schema)) {
          const type = (schema as any)['@type'] || 'Unknown';
          trackSEOEvents.structuredDataLoaded(String(type));
        }
      });
    } catch {}
  }, [projects, i18n.language]);
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(portfolioSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(collectionSchema)}
      </script>
    </Helmet>
  );
};

export default PortfolioStructuredData;